import { useState } from 'react';
import { useAppData } from '../store';
import { EXPENSE_CATEGORY_LABELS, PAID_BY_LABELS } from '../types';
import type { Expense } from '../types';
import { computeBalance } from '../lib/expenses';
import { formatUSD } from '../lib/format';
import * as db from '../lib/db';
import ExpenseForm from './ExpenseForm';

export default function ExpenseSection({ propertyId }: { propertyId: string }) {
  const { expenses, refresh } = useAppData();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);

  const propertyExpenses = expenses
    .filter((e) => e.property_id === propertyId)
    .sort((a, b) => (a.expense_date < b.expense_date ? 1 : -1));

  const balance = computeBalance(propertyExpenses);
  const jointTotal = balance.total - balance.byPayer.dave - balance.byPayer.rich;

  async function handleDelete(expense: Expense) {
    if (!confirm(`Delete this ${formatUSD(expense.amount)} expense?`)) return;
    await db.deleteExpense(expense.id);
    await refresh();
  }

  return (
    <section className="expense-section">
      <div className="section-header">
        <h2>Expenses</h2>
        <button type="button" className="btn-primary small" onClick={() => setAdding(true)}>
          + Add
        </button>
      </div>

      {propertyExpenses.length > 0 && (
        <div className="balance-card card">
          <div className="balance-total">
            <span className="muted">Total spent</span>
            <span className="balance-amount">{formatUSD(balance.total)}</span>
          </div>
          <div className="balance-split">
            {jointTotal > 0 && (
              <div className="balance-payer">
                <span className="muted">Shared account</span>
                <span>{formatUSD(jointTotal)}</span>
              </div>
            )}
            <div className="balance-payer">
              <span className="muted">Dave paid out of pocket</span>
              <span>{formatUSD(balance.byPayer.dave)}</span>
            </div>
            <div className="balance-payer">
              <span className="muted">Rich paid out of pocket</span>
              <span>{formatUSD(balance.byPayer.rich)}</span>
            </div>
          </div>
          {jointTotal > 0 && (
            <p className="balance-owed muted">
              Shared-account spending is assumed already split 50/50 via equal contributions.
            </p>
          )}
          {balance.owedBy && (
            <p className="balance-owed">
              {PAID_BY_LABELS[balance.owedBy]} owes {PAID_BY_LABELS[balance.owedTo!]}{' '}
              <strong>{formatUSD(balance.owedAmount)}</strong> on out-of-pocket spending to
              even the 50/50 split
            </p>
          )}
          {!balance.owedBy && (balance.byPayer.dave > 0 || balance.byPayer.rich > 0) && (
            <p className="balance-owed muted">Out-of-pocket split is even.</p>
          )}
        </div>
      )}

      {adding && (
        <ExpenseForm
          propertyId={propertyId}
          onCancel={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await refresh();
          }}
        />
      )}

      {editing && (
        <ExpenseForm
          propertyId={propertyId}
          existing={editing}
          onCancel={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}

      {propertyExpenses.length === 0 && !adding ? (
        <p className="muted empty">No expenses logged for this property yet.</p>
      ) : (
        <ul className="expense-list">
          {propertyExpenses.map((e) => (
            <li key={e.id} className="expense-row">
              <button type="button" className="expense-open" onClick={() => setEditing(e)}>
                <div className="expense-main">
                  <span className="expense-category">{EXPENSE_CATEGORY_LABELS[e.category]}</span>
                  {e.payee && <span className="muted"> · {e.payee}</span>}
                  <div className="muted expense-sub">
                    {e.expense_date} · Paid by {PAID_BY_LABELS[e.paid_by]}
                  </div>
                </div>
                <span className="expense-amount">{formatUSD(e.amount)}</span>
              </button>
              <button
                type="button"
                className="attachment-delete"
                onClick={() => handleDelete(e)}
                aria-label="Delete expense"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
